import {md2html} from '@github-ui/md2html'
import {
  createMutationId,
  dispatchMutationLifecycleEvent,
  MutationLifecycleEvent,
  type ObjectPropertiesPatch,
  UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
} from '@github-ui/mutation-lifecycle-events'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  updateIssueBodyMutation,
  updateIssueBodyMutation$data,
  updateIssueBodyMutation$rawResponse,
  UpdateIssueInput,
} from './__generated__/updateIssueBodyMutation.graphql'

/** Packages a mutation response into the `{id, values}` event payload shape. */
function toObjectPropertiesPatch(
  data: updateIssueBodyMutation$data | updateIssueBodyMutation$rawResponse,
): ObjectPropertiesPatch | undefined {
  const issue = data.updateIssue?.issue
  if (!issue) return undefined

  const {id, ...values} = issue

  return {
    id,
    values,
  }
}

type commitUpdateIssueBodyMutationProps = {
  environment: Environment
  input: {
    issueId: string
    body: string
    bodyVersion: string
  }
  onCompleted?: (response: updateIssueBodyMutation$data) => void
  onError?: (error: Error) => void
  withOptimisticResponse?: boolean
}

export function commitUpdateIssueBodyMutation({
  environment,
  input: {issueId, body, bodyVersion},
  onCompleted,
  onError,
  withOptimisticResponse = false,
}: commitUpdateIssueBodyMutationProps) {
  const inputHash: UpdateIssueInput = {
    id: issueId,
    body,
    bodyVersion,
  }

  const optimisticResponse: updateIssueBodyMutation$rawResponse | undefined = withOptimisticResponse
    ? {
        updateIssue: {
          issue: {
            id: issueId,
            body,
            bodyVersion,
            bodyHTML: md2html(body),
            lastEditedAt: new Date().toISOString(),
            viewerCanReadUserContentEdits: true,
            lastUserContentEdit: null,
          },
        },
      }
    : undefined

  const optimisticPatch = optimisticResponse && toObjectPropertiesPatch(optimisticResponse)
  const optimisticEvent =
    optimisticPatch &&
    new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'optimistic', data: optimisticPatch})
  const mutationId = optimisticEvent?.detail.mutationId ?? createMutationId()

  const disposable = commitMutation<updateIssueBodyMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation updateIssueBodyMutation($input: UpdateIssueInput!) @raw_response_type {
        updateIssue(input: $input) {
          issue {
            id
            bodyVersion
            body
            bodyHTML(unfurlReferences: true)
            viewerCanReadUserContentEdits
            lastEditedAt
            lastUserContentEdit {
              editor {
                url
                login
              }
            }
          }
        }
      }
    `,
    optimisticResponse,
    variables: {
      input: inputHash,
    },
    onCompleted: (response: updateIssueBodyMutation$data) => {
      const patch = toObjectPropertiesPatch(response)
      if (patch) {
        dispatchMutationLifecycleEvent(
          new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
            phase: 'completed',
            mutationId,
            data: patch,
          }),
        )
      }
      if (onCompleted) onCompleted(response)
    },
    onError: error => {
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'error', mutationId, error}),
      )
      if (onError) onError(error)
    },
  })

  if (optimisticEvent) {
    dispatchMutationLifecycleEvent(optimisticEvent)
  }

  return disposable
}
